import { Injectable } from '@angular/core'
import { CampaignSource } from '@parkupp/core'
import { GlobalService } from './global.service'

export interface CampaignData {
    p_source?: string
    p_campaign?: string
    p_lead_user?: string
    gclid?: string
    gbraid?: string
    wbraid?: string
    googleClickCapturedAt?: string
}

@Injectable({
    providedIn: 'root',
})
export class CampaignTrackingService {
    private readonly SOURCE_COOKIE_NAME = 'parkupp_source'
    private readonly CAMPAIGN_COOKIE_NAME = 'parkupp_campaign'
    private readonly LEAD_USER_COOKIE_NAME = 'parkupp_lead_user'
    private readonly GCLID_COOKIE_NAME = 'parkupp_gclid'
    private readonly GBRAID_COOKIE_NAME = 'parkupp_gbraid'
    private readonly WBRAID_COOKIE_NAME = 'parkupp_wbraid'
    private readonly CLICK_BUNDLE_COOKIE_NAME = 'parkupp_google_click'
    private readonly AFFILIATE_COOKIE_NAME = 'parkupp_aff'
    private readonly COOKIE_EXPIRY_DAYS = 90
    private readonly EXPIRY_MS = this.COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000

    /** Affiliate code present on the landing URL of this page load (counted as a click). */
    landingAffiliateCode: string | null = null

    constructor(private globalService: GlobalService) {}

    /**
     * Initialize campaign tracking by checking URL parameters and storing them.
     *
     * p_source + p_campaign: first-touch as a pair (never overwritten, never mixed across visits).
     * p_lead_user: first-touch on its own (CRM assignment, not marketing attribution).
     * aff: affiliate code, first-touch. Also tags the visit as source "Affiliate" / campaign <code>
     * when no source is known yet, so affiliate traffic shows in the existing campaign reports.
     * gclid / gbraid / wbraid: last-touch (always overwritten) because Google
     * expects the most recent click to receive the conversion credit.
     *
     * Everything is mirrored to localStorage because Safari ITP caps JS-set cookies at 7 days.
     *
     * Standard utm_source / utm_campaign are mapped onto p_source / p_campaign
     * when the custom params are absent, so Google Ads auto-tagged traffic
     * populates the existing attribution reports without extra URL work.
     */
    initTracking(): void {
        const urlParams = new URLSearchParams(window.location.search)

        const affiliateCode = this.normaliseAffiliateCode(urlParams.get('aff'))
        this.landingAffiliateCode = affiliateCode
        if (affiliateCode) {
            if (!this.getAffiliateCode()) {
                this.setValue(this.AFFILIATE_COOKIE_NAME, `${affiliateCode}|${Date.now()}`)
                console.log(`[Campaign Tracking] Stored affiliate code (first-touch): ${affiliateCode}`)
            }
        }

        // --- First-touch attribution (p_source + p_campaign as a pair) ---
        const pSource = urlParams.get('p_source') || urlParams.get('utm_source') || (affiliateCode ? CampaignSource.AFFILIATE : null)
        const pCampaign = urlParams.get('p_campaign') || urlParams.get('utm_campaign') || (affiliateCode && !urlParams.get('p_source') ? affiliateCode : null)
        const pLeadUser = urlParams.get('p_lead_user')

        const existingSource = this.getValue(this.SOURCE_COOKIE_NAME)
        const existingCampaign = this.getValue(this.CAMPAIGN_COOKIE_NAME)

        if ((pSource || pCampaign) && !existingSource && !existingCampaign) {
            if (pSource) this.setValue(this.SOURCE_COOKIE_NAME, pSource)
            if (pCampaign) this.setValue(this.CAMPAIGN_COOKIE_NAME, pCampaign)
            console.log(`[Campaign Tracking] Stored source/campaign (first-touch): ${pSource} / ${pCampaign}`)
        } else if (pSource || pCampaign) {
            console.log(`[Campaign Tracking] Already tracked as ${existingSource} / ${existingCampaign}, not overwriting with ${pSource} / ${pCampaign}`)
        }

        if (pLeadUser && !this.getValue(this.LEAD_USER_COOKIE_NAME)) {
            this.setValue(this.LEAD_USER_COOKIE_NAME, pLeadUser)
            console.log(`[Campaign Tracking] Stored lead user (first-touch): ${pLeadUser}`)
        }

        // Replace the complete click bundle on a new ad visit, clearing older identifier types.
        const click: CampaignData = {}
        for (const name of ['gclid', 'gbraid', 'wbraid'] as const) {
            const value = urlParams.get(name)
            if (value) click[name] = value
        }
        if (click.gclid || click.gbraid || click.wbraid) {
            click.googleClickCapturedAt = new Date().toISOString()
            this.setValue(this.CLICK_BUNDLE_COOKIE_NAME, JSON.stringify(click))
            for (const name of [this.GCLID_COOKIE_NAME, this.GBRAID_COOKIE_NAME, this.WBRAID_COOKIE_NAME]) {
                this.globalService.deleteCookie(name)
                try { localStorage.removeItem(name) } catch (_) {}
            }
        }
    }

    /**
     * Get current campaign tracking data from cookies (with localStorage fallback).
     */
    getCampaignData(): CampaignData {
        const result: CampaignData = {}
        const fields: [keyof CampaignData, string][] = [
            ['p_source', this.SOURCE_COOKIE_NAME],
            ['p_campaign', this.CAMPAIGN_COOKIE_NAME],
            ['p_lead_user', this.LEAD_USER_COOKIE_NAME],
        ]
        for (const [field, name] of fields) {
            const value = this.getValue(name)
            if (value) result[field] = value
        }
        const bundle = this.getValue(this.CLICK_BUNDLE_COOKIE_NAME)
        try {
            const click = bundle ? JSON.parse(bundle) : null
            const captured = Date.parse(click?.googleClickCapturedAt)
            if (Number.isFinite(captured) && captured <= Date.now() && Date.now() - captured <= this.EXPIRY_MS) {
                for (const name of ['gclid', 'gbraid', 'wbraid', 'googleClickCapturedAt'] as const) {
                    if (typeof click[name] === 'string') result[name] = click[name]
                }
                return result
            }
            // Migrate timestamped legacy storage without renewing its age. Untimed IDs cannot
            // safely be assigned a new capture date; page-view history remains a server fallback.
            const legacy = [this.GCLID_COOKIE_NAME, this.GBRAID_COOKIE_NAME, this.WBRAID_COOKIE_NAME]
                .map((name, index) => ({ index, match: /^(\d{13})~(.*)$/.exec(localStorage.getItem(name) || '') }))
                .filter(item => item.match && Number(item.match[1]) <= Date.now() && Date.now() - Number(item.match[1]) <= this.EXPIRY_MS)
                .sort((a, b) => Number(b.match![1]) - Number(a.match![1]))[0]
            if (legacy?.match) {
                result[(['gclid', 'gbraid', 'wbraid'] as const)[legacy.index]] = legacy.match[2]
                result.googleClickCapturedAt = new Date(Number(legacy.match[1])).toISOString()
            }
        } catch (_) {}
        return result
    }

    /** First-touch affiliate code, if captured within the attribution window. */
    getAffiliateCode(): string | null {
        const raw = this.getValue(this.AFFILIATE_COOKIE_NAME)
        if (!raw) return null
        const [code, at] = raw.split('|')
        if (at && Date.now() - Number(at) > this.EXPIRY_MS) return null
        return this.normaliseAffiliateCode(code)
    }

    /**
     * Clear campaign tracking data
     */
    clearCampaignData(): void {
        for (const name of [
            this.SOURCE_COOKIE_NAME,
            this.CAMPAIGN_COOKIE_NAME,
            this.LEAD_USER_COOKIE_NAME,
            this.GCLID_COOKIE_NAME,
            this.GBRAID_COOKIE_NAME,
            this.WBRAID_COOKIE_NAME,
            this.AFFILIATE_COOKIE_NAME,
            this.CLICK_BUNDLE_COOKIE_NAME,
        ]) {
            this.globalService.deleteCookie(name)
            try {
                localStorage.removeItem(name)
            } catch (_) {}
        }
        console.log('[Campaign Tracking] Cleared campaign data')
    }

    private normaliseAffiliateCode(code: string | null | undefined): string | null {
        const value = (code || '').trim().toUpperCase()
        return /^[A-Z0-9-]{3,20}$/.test(value) ? value : null
    }

    /** Persist a value to both cookie (URI-encoded) and localStorage. */
    private setValue(name: string, value: string): void {
        this.globalService.setCookie({
            name,
            value: encodeURIComponent(value),
            expireDays: this.COOKIE_EXPIRY_DAYS,
        })
        try {
            // Timestamped so the localStorage copy expires with the cookie
            localStorage.setItem(name, `${Date.now()}~${value}`)
        } catch (_) {}
    }

    /** Resolve a value: cookie first, then localStorage fallback. */
    private getValue(name: string): string | null {
        const fromCookie = this.getCookie(name)
        if (fromCookie) {
            try {
                return decodeURIComponent(fromCookie)
            } catch (_) {
                return fromCookie
            }
        }
        try {
            const stored = localStorage.getItem(name)
            if (!stored) return null
            const match = /^(\d{13})~(.*)$/.exec(stored)
            if (!match) return stored // legacy untimestamped click ID
            if (Date.now() - Number(match[1]) > this.EXPIRY_MS) {
                localStorage.removeItem(name)
                return null
            }
            return match[2] || null
        } catch (_) {
            return null
        }
    }

    private getCookie(name: string): string | null {
        const nameEQ = name + '='
        const ca = document.cookie.split(';')
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i]
            while (c.charAt(0) === ' ') c = c.substring(1, c.length)
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
        }
        return null
    }
}
